// T-407: CODA DI CARICO con estrazione lotto/scadenza in background.
//
// L'operatore, per ogni articolo: sceglie prodotto + quantità, scatta 1+ foto
// dell'etichetta e lo "mette in coda" — poi passa subito all'articolo dopo,
// SENZA aspettare l'estrazione. Un worker in background:
//   1. carica (salva) la foto su `lot-photos`,
//   2. estrae lotto+scadenza via edge lot_expiry (no-thinking, veloce),
//   3. emette lo stock_in GIÀ COMPLETO (qty + lotto + scadenza + foto).
// Un solo evento immutabile per carico → catena hash pulita (niente eventi
// compensativi). Se l'etichetta non è leggibile → 'needs_fix' (inserimento
// manuale). Errori di rete → retry automatico. La coda è persistita
// (Preferences) così non si perde nulla se l'app si chiude.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Preferences } from '@capacitor/preferences';
import {
  extractLotExpiryFromImages,
  uploadLotPhoto,
  downloadLotPhotoB64,
} from './labelExtract.js';

export type QueueStatus = 'queued' | 'processing' | 'done' | 'needs_fix' | 'error';

export interface PendingLoad {
  id: string;
  foodId: string;
  foodName: string;
  qty: number;
  uom: string;
  storageUnitId: string | null;
  /** base64 in memoria (NON persistito). */
  photosB64: string[];
  /** storage path su lot-photos (persistito). */
  photoPaths: string[];
  status: QueueStatus;
  batchLot: string | null;
  expirationDate: string | null;
  attempts: number;
  error?: string;
  createdAt: number;
}

export interface EmitStockInInput {
  foodId: string;
  qty: number;
  uom: string;
  storageUnitId: string | null;
  batchLot: string | null;
  expirationDate: string | null;
  photoPath: string | null;
}

interface Deps {
  tenantId: string | null;
  businessId: string | null;
  /** Emette lo stock_in completo (lo wireup vive in WarehousePage). */
  emitStockIn: (input: EmitStockInInput) => Promise<void>;
}

/** Shape persistita: senza i base64 (troppo grandi), solo path + metadati. */
type Persisted = Omit<PendingLoad, 'photosB64'>;

const MAX_ATTEMPTS = 5;
const keyFor = (tenantId: string): string => `label_load_queue:${tenantId}`;

function toPersisted(i: PendingLoad): Persisted {
  return {
    id: i.id,
    foodId: i.foodId,
    foodName: i.foodName,
    qty: i.qty,
    uom: i.uom,
    storageUnitId: i.storageUnitId,
    photoPaths: i.photoPaths,
    status: i.status,
    batchLot: i.batchLot,
    expirationDate: i.expirationDate,
    attempts: i.attempts,
    error: i.error,
    createdAt: i.createdAt,
  };
}

export function useLabelLoadQueue(deps: Deps) {
  const { tenantId, businessId, emitStockIn } = deps;
  const [queue, setQueue] = useState<PendingLoad[]>([]);
  const queueRef = useRef<PendingLoad[]>([]);
  const busyRef = useRef(false);
  const loadedRef = useRef(false);

  // Mantieni il ref sincronizzato + persisti (escludendo i 'done' e i base64).
  useEffect(() => {
    queueRef.current = queue;
    if (!tenantId || !loadedRef.current) return;
    const persisted = queue.filter((i) => i.status !== 'done').map(toPersisted);
    void Preferences.set({ key: keyFor(tenantId), value: JSON.stringify(persisted) });
  }, [queue, tenantId]);

  // Ripristina la coda persistita al mount / cambio tenant.
  useEffect(() => {
    if (!tenantId) return;
    loadedRef.current = false;
    let alive = true;
    void (async () => {
      const { value } = await Preferences.get({ key: keyFor(tenantId) });
      if (!alive) return;
      const arr: Persisted[] = value ? (JSON.parse(value) as Persisted[]) : [];
      const restored: PendingLoad[] = arr.map((p) => {
        const lostPhoto = p.photoPaths.length === 0; // niente base64 in memoria + niente path = foto persa
        const status: QueueStatus =
          lostPhoto && p.status !== 'needs_fix'
            ? 'error'
            : p.status === 'processing'
              ? 'queued'
              : p.status;
        return {
          ...p,
          photosB64: [],
          status,
          error: lostPhoto && p.status !== 'needs_fix'
            ? 'Foto persa alla chiusura app — riscatta.'
            : p.error,
        };
      });
      loadedRef.current = true;
      setQueue(restored);
    })();
    return () => {
      alive = false;
    };
  }, [tenantId]);

  const patch = useCallback((id: string, p: Partial<PendingLoad>) => {
    setQueue((q) => q.map((it) => (it.id === id ? { ...it, ...p } : it)));
  }, []);

  const remove = useCallback((id: string) => {
    setQueue((q) => q.filter((it) => it.id !== id));
  }, []);

  // Carica (salva) subito le foto su storage, appena aggiunto l'articolo.
  const ensureUploaded = useCallback(
    async (id: string) => {
      if (!tenantId) return;
      const it = queueRef.current.find((x) => x.id === id);
      if (!it || it.photoPaths.length > 0 || it.photosB64.length === 0) return;
      try {
        const paths: string[] = [];
        for (const b of it.photosB64) paths.push(await uploadLotPhoto(tenantId, b));
        patch(id, { photoPaths: paths });
      } catch {
        /* riprova il worker */
      }
    },
    [tenantId, patch],
  );

  const addToQueue = useCallback(
    (input: {
      foodId: string;
      foodName: string;
      qty: number;
      uom: string;
      storageUnitId: string | null;
      photosB64: string[];
    }) => {
      const item: PendingLoad = {
        id: crypto.randomUUID(),
        ...input,
        photoPaths: [],
        status: 'queued',
        batchLot: null,
        expirationDate: null,
        attempts: 0,
        createdAt: Date.now(),
      };
      setQueue((q) => [...q, item]);
      void ensureUploaded(item.id);
    },
    [ensureUploaded],
  );

  // Processa un item: assicura la foto salvata → estrae → emette lo stock_in.
  const processItem = useCallback(
    async (item: PendingLoad) => {
      if (!tenantId) return;
      patch(item.id, { status: 'processing' });
      try {
        let paths = item.photoPaths;
        if (paths.length === 0 && item.photosB64.length > 0) {
          paths = [];
          for (const b of item.photosB64) paths.push(await uploadLotPhoto(tenantId, b));
          patch(item.id, { photoPaths: paths });
        }
        let images = item.photosB64;
        if (images.length === 0) {
          if (paths.length === 0) {
            patch(item.id, { status: 'error', error: 'Foto mancante — riscatta.' });
            return;
          }
          images = [];
          for (const p of paths) images.push(await downloadLotPhotoB64(p));
        }
        const { batch_lot, expiration_date } = await extractLotExpiryFromImages(images, businessId);
        if (batch_lot || expiration_date) {
          await emitStockIn({
            foodId: item.foodId,
            qty: item.qty,
            uom: item.uom,
            storageUnitId: item.storageUnitId,
            batchLot: batch_lot,
            expirationDate: expiration_date,
            photoPath: paths[0] ?? null,
          });
          patch(item.id, { status: 'done', batchLot: batch_lot, expirationDate: expiration_date });
        } else {
          patch(item.id, { status: 'needs_fix', photoPaths: paths });
        }
      } catch (err) {
        const attempts = (item.attempts ?? 0) + 1;
        if (attempts >= MAX_ATTEMPTS) {
          patch(item.id, {
            status: 'error',
            attempts,
            error: err instanceof Error ? err.message : 'Errore',
          });
        } else {
          patch(item.id, { status: 'queued', attempts });
        }
      }
    },
    [tenantId, businessId, emitStockIn, patch],
  );

  // Worker: ogni ~1.2s prende il prossimo 'queued' (se online e non occupato).
  useEffect(() => {
    const tick = async () => {
      if (busyRef.current) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      const next = queueRef.current.find((i) => i.status === 'queued');
      if (!next) return;
      busyRef.current = true;
      try {
        await processItem(next);
      } finally {
        busyRef.current = false;
      }
    };
    const iv = setInterval(() => {
      void tick();
    }, 1200);
    return () => clearInterval(iv);
  }, [processItem]);

  // Inserimento manuale quando l'etichetta non è leggibile (needs_fix / error).
  const commitManual = useCallback(
    async (id: string, lot: string | null, exp: string | null) => {
      const it = queueRef.current.find((x) => x.id === id);
      if (!it) return;
      await emitStockIn({
        foodId: it.foodId,
        qty: it.qty,
        uom: it.uom,
        storageUnitId: it.storageUnitId,
        batchLot: lot,
        expirationDate: exp,
        photoPath: it.photoPaths[0] ?? null,
      });
      patch(id, { status: 'done', batchLot: lot, expirationDate: exp });
    },
    [emitStockIn, patch],
  );

  const retry = useCallback(
    (id: string) => patch(id, { status: 'queued', attempts: 0, error: undefined }),
    [patch],
  );

  const clearDone = useCallback(() => {
    setQueue((q) => q.filter((i) => i.status !== 'done'));
  }, []);

  const pendingCount = queue.filter((i) => i.status === 'queued' || i.status === 'processing').length;

  return { queue, addToQueue, remove, retry, commitManual, clearDone, pendingCount };
}
